import authClient from "src/lib/auth/authClient";
import axiosInstance from "src/config/axios";
import type { UserProfile } from "src/lib/endpoints/auth/auth.type";

/**
 * The /auth/* contract. Shapes here mirror the captured traffic from the
 * backend, not the spec -- where they differ the traffic wins.
 */

export type TokenPair = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // seconds
};

/* The API returns the user row with relations and `primaryStack` merged in
   as one object. The legacy getUserProfile endpoint returned an array with
   the same information split across two elements, and App.tsx used to fold
   that into `extraDetail`; this keeps the stored shape identical so nothing
   downstream can tell which path produced it. */
export const normalizeUser = (user: any): UserProfile => ({
  ...user,
  extraDetail: {
    primaryStack: user?.primaryStack ?? null,
    totalUnreadNotifications: user?.totalUnreadNotifications ?? 0,
  },
});

/**
 * Trade a one-time handoff code for a token pair plus the profile.
 * Rejects with the axios error on failure; callers inspect
 * `error.response.data.error.code` (CODE_INVALID | CODE_EXPIRED | CODE_USED).
 */
export const exchangeCode = async (
  code: string
): Promise<TokenPair & { user: UserProfile }> => {
  const { data } = await authClient.post("/auth/exchange", { code });
  const payload = data?.data ?? {};
  return {
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    expiresIn: payload.expiresIn,
    user: normalizeUser(payload.user),
  };
};

/**
 * Re-hydrate the profile for a session we already hold tokens for. Goes
 * through the configured instance on purpose: a stale access token 401s,
 * the interceptor refreshes, and the request is replayed transparently.
 */
export const fetchMe = async (): Promise<UserProfile> => {
  const { data } = await axiosInstance.get("/auth/me");
  return normalizeUser(data?.data);
};

/**
 * Revoke the refresh-token family server-side. Fire-and-forget by design:
 * the endpoint is idempotent and always 200s, and logout must not block on
 * the network -- the local credentials are cleared regardless.
 */
export const revokeSession = (refreshToken: string): void => {
  authClient.post("/auth/logout", { refreshToken }).catch(() => {
    /* Best effort. The next /auth/refresh with this token will be refused
       anyway once the server sees it, and a failed revoke must not keep the
       user on a page they asked to leave. */
  });
};
